import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  MenuItem,
  Menu,
  CircularProgress,
  Modal,
  Button,
} from '@mui/material';
import {
  North as UpIcon,
  Close as CloseIcon,
  MoreHoriz,
  // Add as AddIcon,
} from '@mui/icons-material';
import { ChatMessage, VersionData } from './IdeationService';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import Loader from '../../common/components/Loader';
import { formatTimestamp } from '../SimulationXPLR/utils';
import { uploadFile } from '../IdeationCOI/IdeationService';
import { UploadedFile } from '../../slices/ideationSlice/newIdeationSlice';
import { API_BASE } from '../../config/env';
import { getFileIcon, validateFile } from '../../common/utils';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ShowToast, { ToastTypeEnum } from '../../common/components/ShowToast';
import { FileViewer } from '../NewSolutionXPLR/GeneralInformation/KeyInformation';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import Utils from '../../utility';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CustomTooltip from '../../common/components/CustomToolTip/CustomToolTip';
import { fieldAPIKeyMap } from '../NewIdeationXPLR/NewIdeation/constants';

interface CoiChatProps {
  width?: number;
  onWidthChange?: (width: number) => void;
  onVersionChange?: (version: string, fileSummary: string) => void;
  currentVersion?: string;
  handleSendMessage: (input: string, isFirst?: boolean, id?: string) => void;
  messages: ChatMessage[];
  setMessages: (msgs: ChatMessage[]) => void;
  isSending: boolean;
  inputMessage: string;
  setInputMessage: (msg: string) => void;
  useCaseDetails: Record<string, any>;
  versionData: VersionData;
  handleBack: () => void;
  field: string;
  setContextState: (state: any) => void;
  contextState: any;
  setChatId: (id: string) => void;
  initialChatId?: string;
  chatId?: string;
  setuseCaseDetails: (details: Record<string, any>) => void;
  onRefetchSessions?: () => void;
  handleNewChat: () => void;
  setUploadingImage?: (val: boolean) => void;
}

const CoiChat = ({
  width = 600,
  onWidthChange,
  onVersionChange,
  handleSendMessage,
  messages,
  isSending,
  inputMessage = '',
  setInputMessage,
  useCaseDetails,
  field = '',
  setContextState,
  contextState,
  versionData,
  initialChatId,
  chatId,
  setuseCaseDetails,
  onRefetchSessions,
  handleNewChat,
  setUploadingImage = () => { },
}: CoiChatProps) => {
  const dropdownOptions = ['Add file'];
  const [isLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sessionMenuAnchor, setSessionMenuAnchor] = useState<null | HTMLElement>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const resizeRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isResizing = useRef(false);
  const [filePreview, setFilePreview] = useState<{ url: string; fileName?: string } | null>(null);

  const user = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    });
  }, [messages]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputMessage);
    }
  };

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
  };

  const handleVersionSelect = (version: string, versionFileSummary: string = '') => {
    if (onVersionChange) {
      onVersionChange(version, versionFileSummary);
    }
  };

  // const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
  //   setAnchorEl(event.currentTarget);
  // };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSessionMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSessionMenuAnchor(event.currentTarget);
  };

  const handleSessionMenuClose = () => {
    setSessionMenuAnchor(null);
  };

  const handleRenameClick = () => {
    setNewSessionName(useCaseDetails?.title || '');
    setSessionMenuAnchor(null);
    setRenameDialogOpen(true);
  };

  const handleDeleteClick = () => {
    setSessionMenuAnchor(null);
    setDeleteDialogOpen(true);
  };

  const handleRenameSubmit = async () => {
    const organization_id = localStorage.getItem('current_organization');
    const token = Utils.getCookie('auth-token');

    try {
      const response = await fetch(
        `${API_BASE}/api/v1/ideation/query-history/${chatId}/session-name/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            session_name: newSessionName,
            field_type: fieldAPIKeyMap[field],
            organization_id,
          }),
        }
      );

      if (response.ok) {
        setuseCaseDetails?.({ ...useCaseDetails, title: newSessionName });
        ShowToast({
          message: 'Session renamed successfully',
          type: ToastTypeEnum.Success,
        });
        setRenameDialogOpen(false);
        onRefetchSessions?.();
      } else {
        ShowToast({
          message: 'Failed to rename session',
          type: ToastTypeEnum.Error,
        });
      }
    } catch (error) {
      console.error('Error renaming session:', error);
      ShowToast({
        message: 'Error renaming session',
        type: ToastTypeEnum.Error,
      });
    }
  };

  const handleDeleteConfirm = async () => {
    const organization_id = localStorage.getItem('current_organization');
    const token = Utils.getCookie('auth-token');

    try {
      const response = await fetch(`${API_BASE}/api/v1/ideation/query-history/${chatId}/delete/`, {
        method: 'DELETE',
        headers: {
          authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          organization_id,
          field_type: fieldAPIKeyMap[field],
        }),
      });

      if (response.ok) {
        ShowToast({
          message: 'Session deleted successfully',
          type: ToastTypeEnum.Success,
        });
        setDeleteDialogOpen(false);
        onRefetchSessions?.();
        navigate('/data-xplr');
        handleNewChat();
      } else {
        ShowToast({
          message: 'Failed to delete session',
          type: ToastTypeEnum.Error,
        });
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      ShowToast({
        message: 'Error deleting session',
        type: ToastTypeEnum.Error,
      });
    }
  };

  const handleDropdownSelect = (option: string) => {
    handleMenuClose();
    if (option === 'Add file') {
      fileInputRef.current?.click();
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const filesArray = Array.from(files);

      // Validate files before processing
      const validatedFiles: { file: File; index: number }[] = [];

      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i] as File;
        const validation = await validateFile(file);

        if (!validation.ok) {
          ShowToast({
            message: `${file.name}: ${validation.error}`,
            type: ToastTypeEnum.Error,
          });
        } else {
          validatedFiles.push({ file, index: i });
        }
      }

      // Only proceed with valid files
      if (validatedFiles.length === 0) {
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Create temporary file objects with uploading state
      const tempFiles: UploadedFile[] = validatedFiles.map(({ file, index }) => ({
        id: `file-${Date.now()}-${index}`,
        name: file.name,
        file,
        isUploading: true,
      }));

      // Add files to state immediately
      setContextState((prev: any) => ({
        ...prev,
        uploadedFiles: [...prev.uploadedFiles, ...tempFiles],
      }));

      setUploadingImage(true);

      // Upload files and get URLs
      for (let i = 0; i < tempFiles.length; i++) {
        const tempFile = tempFiles[i];
        try {
          const response = await uploadFile(tempFile.file);

          if (response && response.files && response.files.length > 0) {
            const fileUrl = response.files[0];

            // Update the file with URL and remove uploading state
            // Store the file path for later analysis
            setContextState((prev: any) => ({
              ...prev,
              uploadedFiles: prev.uploadedFiles.map((f: UploadedFile) =>
                f.id === tempFile.id
                  ? {
                    ...f,
                    url: fileUrl,
                    isUploading: false,
                    filePath: response.files[0], // Store file path for analysis
                    fileSize: `${Math.round(tempFile.file.size / 1024)}KB`,
                  }
                  : f
              ),
            }));
          } else {
            // Remove file if upload failed
            setContextState((prev: any) => ({
              ...prev,
              uploadedFiles: prev.uploadedFiles.filter((f: UploadedFile) => f.id !== tempFile.id),
            }));
            ShowToast({
              message: `Upload failed for ${tempFile.name}`,
              type: ToastTypeEnum.Error,
            });
            console.error('File upload failed for:', tempFile.name);
          }

          setUploadingImage(false);
        } catch (error) {
          setUploadingImage(false);
          // Remove file if upload failed
          setContextState((prev: any) => ({
            ...prev,
            uploadedFiles: prev.uploadedFiles.filter((f: UploadedFile) => f.id !== tempFile.id),
          }));
          ShowToast({
            message: `Error uploading ${tempFile.name}`,
            type: ToastTypeEnum.Error,
          });
          console.error('Error uploading file:', tempFile.name, error);
        }
      }
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setContextState((prev: any) => ({
      ...prev,
      uploadedFiles: prev.uploadedFiles.filter((f: UploadedFile) => f.id !== fileId),
    }));
  };

  const handleRemoveContext = (contextName: string) => {
    setContextState((prev: any) => ({
      ...prev,
      selectedContexts: prev.selectedContexts.filter((c: string) => c !== contextName),
    }));
  };

  const getProcessNameById = (processId: string): string => {
    if (!contextState.contextProcesses) return `Context: ${processId}`;
    const process = contextState.contextProcesses.find((p: any) => p.id === processId);
    return process ? process.name : processId;
  };

  // Resizable functionality
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current || !containerRef.current) return;

      // Get the actual left position of the container
      const containerLeft = containerRef.current.getBoundingClientRect().left;
      const newWidth = e.clientX - containerLeft;

      if (newWidth >= 400 && newWidth <= 800) {
        if (onWidthChange) {
          onWidthChange(newWidth);
        }
      }
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [onWidthChange]);

  const handleMouseDown = () => {
    isResizing.current = true;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  };

  const getUserInitials = (username?: string): string => {
    if (!username)
      return `${user?.first_name?.charAt(0)?.toUpperCase()}${user?.last_name?.charAt(0)?.toUpperCase()}`;
    const parts = username.split(/[.\s]+/).filter(Boolean);

    if (parts.length === 0) return 'U';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

    const initials = parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
    return initials.toUpperCase();
  };

  const isSendDisabled = useMemo(() => {
    const messageEmpty = !inputMessage.trim();

    const hasFiles = contextState?.uploadedFiles?.length > 0;
    // const hasContexts = contextState?.selectedContexts?.length > 0;

    const anyFileUploading = contextState?.uploadedFiles?.some((file: any) => file?.isUploading);
    if (anyFileUploading) return true;

    if (isSending) return true;

    if (messageEmpty && !hasFiles) return true;

    return false;
  }, [inputMessage, contextState?.uploadedFiles, contextState?.selectedContexts, isSending]);

  const handlePaste = async (event: React.ClipboardEvent) => {
    if (!event.clipboardData) return;

    const items = event.clipboardData.items;
    if (!items) return;

    const imageItem = Array.from(items).find((item) => item.type.startsWith('image/'));

    if (imageItem) {
      event.preventDefault();

      const file = imageItem.getAsFile();
      if (!file) return;

      const validation = await validateFile(file);
      if (!validation.ok) {
        ShowToast({
          message: validation?.error || '',
          type: ToastTypeEnum.Error,
        });
        return;
      }

      const tempFile: UploadedFile = {
        id: `pasted-${Date.now()}`,
        name: file.name || 'pasted-image.png',
        file,
      };

      setContextState((prev: any) => ({
        ...prev,
        uploadedFiles: [...prev.uploadedFiles, tempFile],
      }));

      try {
        setUploadingImage(true);
        const response = await uploadFile(file);

        if ((response?.files || [])?.length > 0) {
          const fileUrl = response?.files?.[0];

          setContextState((prev: any) => ({
            ...prev,
            uploadedFiles: prev.uploadedFiles.map((f: UploadedFile) =>
              f.id === tempFile.id
                ? {
                  ...f,
                  url: fileUrl,
                  isUploading: false,
                  filePath: response?.files[0],
                  fileSize: `${Math.round(file.size / 1024)}KB`,
                }
                : f
            ),
          }));
          setUploadingImage(false);
        }
      } catch {
        ShowToast({
          message: 'Image paste upload failed',
          type: ToastTypeEnum.Error,
        });

        setContextState((prev: any) => ({
          ...prev,
          uploadedFiles: prev.uploadedFiles.filter((f: any) => f.id !== tempFile.id),
        }));
        setUploadingImage(false);
      }

      return;
    }

    const pastedText = event.clipboardData.getData('text');

    if (pastedText) {
      event.preventDefault();

      const input = event.target as HTMLTextAreaElement;
      const start = input.selectionStart;
      const end = input.selectionEnd;

      const newValue = inputMessage.slice(0, start) + pastedText + inputMessage.slice(end);

      setInputMessage(newValue);
    }
  };

  const location = useLocation();
  useEffect(() => {
    const isSkipChat = location.state?.skipChat;
    if (!initialChatId && !isSkipChat) {
      const uuid = uuidv4();
      handleSendMessage('', true, uuid);
    }
  }, []);

  const formatFileSize = (size: string) => {
    if (!!size) {
      const sizeNumber = parseInt(size.replace('KB', ''));
      if (sizeNumber > 1024) {
        return `${(sizeNumber / 1024).toFixed(2)} mb`;
      } else {
        return `${sizeNumber} kb`;
      }
    }
    return size;
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          width,
          height: 'calc(100vh - 100px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loader />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        width,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Chat Header */}
      <Paper
        elevation={0}
        sx={{
          p: '12px',
          borderRadius: '12px 12px 0 0',
          borderBottom: '1px solid #E1E1E1',
          borderColor: 'divider',
          backgroundColor: '#fff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '15px',
                color: '#333333',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {`${useCaseDetails?.title || 'AI Opportunity Discovery'}`}
            </Typography>
            <IconButton
              size="small"
              onClick={handleSessionMenuOpen}
              sx={{
                color: '#6b7280',
                padding: '4px',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: '#e5e7eb',
                },
              }}
            >
              <MoreHoriz fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={sessionMenuAnchor}
              open={Boolean(sessionMenuAnchor)}
              onClose={handleSessionMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleRenameClick} sx={{ fontSize: '14px' }}>
                Rename
              </MenuItem>
              <MenuItem onClick={handleDeleteClick} sx={{ fontSize: '14px', color: '#d32f2f' }}>
                Delete
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Paper>

      {/* Messages Area */}
      <Paper
        ref={messagesContainerRef}
        elevation={0}
        sx={{
          flex: 1,
          overflowY: 'auto',
          py: 2,
          px: 1.5,
          backgroundColor: '#fff',
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#ccc',
            borderRadius: '3px',
          },
        }}
      >
        {(messages || [])?.map((message, index) => (
          <React.Fragment key={message.id || index}>
            {/* USER MESSAGE */}
            {(!!message?.query?.user_query ||
              (Array.isArray(message?.files?.data) && message?.files?.data.length > 0) ||
              (Array.isArray(message?.context_knowledge?.data) &&
                message?.context_knowledge?.data.length > 0)) && (
                <Box
                  sx={{
                    mb: 2,
                    mr: 2,
                    display: 'flex',
                    gap: 1,
                    cursor: 'pointer',
                    alignItems: 'flex-start',
                  }}
                >
                  <CustomTooltip
                    variant={'left'}
                    title={
                      message?.username
                        ? message.username
                          .split(/[.\s]+/)
                          .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
                          .join(' ')
                        : `${user?.first_name
                          ? user.first_name.charAt(0).toUpperCase() +
                          user.first_name.slice(1).toLowerCase()
                          : ''
                          } ${user?.last_name
                            ? user.last_name.charAt(0).toUpperCase() +
                            user.last_name.slice(1).toLowerCase()
                            : ''
                          }`.trim()
                    }
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: '#A3BBFD',
                        fontSize: '14px',
                      }}
                    >
                      {getUserInitials(message.username)}
                    </Avatar>
                  </CustomTooltip>

                  <Box sx={{ width: '85%' }}>
                    {message?.query?.user_query && (
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: '100%',
                          backgroundColor: '#F2F5FF',
                          borderRadius: '12px',
                          p: 2,
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                          overflow: 'hidden',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#333',
                            fontSize: '14px',
                            lineHeight: 1.6,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '100%',
                            overflow: 'hidden',
                          }}
                        >
                          {message?.query?.user_query || ''}
                        </Typography>
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        maxWidth: '100%',
                        mt: 1,
                      }}
                    >
                      {Array.isArray(message?.files?.data) &&
                        (message?.query?.user_file_data || message?.files?.data || [])?.map(
                          (file: any) => (
                            <Box
                              key={file.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                border: '1px solid #E0E0E0',
                                borderRadius: '4px',
                                bgcolor: '#ffffff',
                                px: 1,
                                py: 0.5,
                                minWidth: 180,
                                maxWidth: '308px',
                                opacity: file.isUploading || file.isAnalyzing ? 0.6 : 1,
                                cursor: 'pointer',
                                mr: 1,
                                my: 0.5,
                              }}
                              onClick={() => {
                                const fileUrl = file?.fileUrl || file?.url || file?.file_url;
                                if (fileUrl) {
                                  setFilePreview({
                                    url: fileUrl,
                                    fileName: file?.name || file?.fileName,
                                  });
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '6px',
                                  bgcolor: '#f1f3f4',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  overflow: 'hidden',
                                  mr: 1,
                                }}
                              >
                                {file.isUploading || file.isAnalyzing ? (
                                  <CircularProgress size={14} sx={{ color: '#5f6368' }} />
                                ) : (
                                  getFileIcon(file.name, file.file)
                                )}
                              </Box>

                              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: '14px',
                                      color: '#202124',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: '100%',
                                    }}
                                  >
                                    {file.name || file?.fileName}
                                  </Typography>
                                  <img src="/images/arrbtn.svg" alt="arrowbtn" />
                                </Box>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '12px',
                                    color: 'rgba(108, 117, 125, 1)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                  }}
                                >
                                  {formatFileSize(file.fileSize)}
                                </Typography>
                              </Box>
                            </Box>
                          )
                        )}

                      {Array.isArray(message?.context_knowledge?.data) &&
                        (message?.context_knowledge?.data || [])?.map((context: any) => (
                          <Box
                            key={context}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              border: '1px solid #E0E0E0',
                              borderRadius: '12px',
                              bgcolor: '#fafafa',
                              px: 1,
                              py: 0.5,
                              minWidth: 180,
                              maxWidth: '100%',
                              mr: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '6px',
                                bgcolor: '#f1f3f4',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                mr: 1,
                              }}
                            >
                              <AttachFileIcon
                                sx={{ fontSize: 18, color: '#5f6368', transform: 'rotate(45deg)' }}
                              />
                            </Box>

                            <Typography
                              variant="body2"
                              sx={{
                                flex: 1,
                                fontWeight: 500,
                                fontSize: '14px',
                                color: '#202124',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {context?.use_case_title || `Process Context`}
                            </Typography>
                          </Box>
                        ))}
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        color: '#999',
                        fontSize: '11px',
                        mt: 1,
                        display: 'block',
                      }}
                    >
                      {formatTimestamp(message?.created_at || '')}
                    </Typography>
                  </Box>
                </Box>
              )}

            {/* ASSISTANT MESSAGE (only if answer exists) */}
            {((typeof message.answer === 'string' && message.answer.trim() !== '') ||
              (message?.discoveryContent?.file_summary && message?.version)) && (
                <Box
                  sx={{
                    mb: 2,
                    mr: 2,
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: '#fff',
                      fontSize: '14px',
                    }}
                  >
                    <img src="/images/ai-xplr-logo-icon.svg" height={24} width={26} alt="xplr-logo" />
                  </Avatar>

                  <Box sx={{ width: '85%' }}>
                    {message.answer && (
                      <Box
                        sx={{
                          width: '100%',
                          // backgroundColor: '#F9FBFF',
                          borderRadius: '12px',
                          p: 2,
                          // boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                          overflow: 'hidden',
                          pl: 0,
                        }}
                      >
                        <Typography
                          component="div"
                          variant="body2"
                          sx={{
                            color: '#333',
                            fontSize: '14px',
                            lineHeight: 1.5, // 🔽 tighter line spacing
                            whiteSpace: 'normal', // IMPORTANT: let markdown control layout
                            wordBreak: 'break-word',

                            '& p': {
                              margin: '0 0 4px 0', // 🔽 reduce paragraph spacing
                              lineHeight: 1.4,
                            },

                            '& ul, & ol': {
                              paddingLeft: '18px',
                              marginBottom: '4px',
                            },

                            '& li': {
                              marginBottom: '2px',
                              lineHeight: 1.5,
                            },

                            '& pre': {
                              margin: '6px 0',
                            },

                            '& h1, & h2, & h3': {
                              margin: '8px 0 4px',
                              lineHeight: 1.4,
                            },
                          }}
                        >
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.answer}</ReactMarkdown>
                        </Typography>
                      </Box>
                    )}

                    {/* Version info box */}
                    {((message?.discoveryContent?.report && message?.version) ||
                      message?.fileSummary) &&
                      message?.version !== '1.0.0' && (
                        <Box
                          sx={{
                            width: '100%',
                            backgroundColor: '#F1F1F1',
                            borderRadius: '12px',
                            p: '8px 16px',
                            mt: 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                          }}
                          onClick={() =>
                            handleVersionSelect(message?.version || '', message?.fileSummary || '')
                          }
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#131313',
                              fontSize: '16px',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {useCaseDetails?.title ||
                              (message?.discoveryContent?.report
                                ? 'AI Ideation Report'
                                : 'Attachment Summary')}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#757575',
                              fontSize: '16px',
                              whiteSpace: 'pre-wrap',
                              fontWeight: 600,
                            }}
                          >
                            v{message?.version?.split('.')?.[2]}
                          </Typography>
                        </Box>
                      )}

                    <Box
                      sx={{
                        display: 'flex',
                        width: '100%',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: '0px 16px',
                        pl: 0,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#999',
                          fontSize: '11px',
                          mt: 1,
                          display: 'block',
                        }}
                      >
                        {formatTimestamp(message?.updated_at || '')}
                      </Typography>
                      {((message?.discoveryContent?.report && message?.version) ||
                        message?.fileSummary) &&
                        message?.version !== '1.0.0' &&
                        message?.timeTaken > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#999',
                              fontSize: '11px',
                              mt: 0.5,
                              display: 'block',
                            }}
                          >
                            {`Time taken: ${(message?.timeTaken || '0')?.toString()?.substring(0, 4)}s`}
                          </Typography>
                        )}
                    </Box>
                  </Box>
                </Box>
              )}
          </React.Fragment>
        ))}

        {isSending && (
          <Box
            sx={{
              mb: 2,
              mr: 2,
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                backgroundColor: '#fff',
                fontSize: '14px',
              }}
            >
              <img src="/images/ai-xplr-logo-icon.svg" height={32} width={32} alt="xplr-logo" />
            </Avatar>

            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', pt: 1 }}>
              {[0, 1, 2].map((index) => (
                <Box
                  key={index}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: '#1A55F9',
                    animation: 'blink 1.4s infinite',
                    animationDelay: `${index * 0.2}s`,
                    '@keyframes blink': {
                      '0%, 60%, 100%': {
                        opacity: 0.3,
                      },
                      '30%': {
                        opacity: 1,
                      },
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Paper>

      {/* Input Area */}
      <Paper
        elevation={0}
        sx={{
          pt: 2,
          px: 1.5,
          pb: 0.5,
          backgroundColor: '#fff',
          borderRadius: '0 0 12px 12px',
        }}
      >
        {!isLoading &&
          !isSending &&
          inputMessage.trim() === '' &&
          contextState?.uploadedFiles?.length === 0 &&
          contextState?.selectedContexts?.length === 0 &&
          Object.keys(versionData || {})?.length === 0 && (
            <Box
              sx={{
                backgroundColor: '#EDEEFF',
                margin: '0px 8px',
                p: '4px',
                borderRadius: '8px 8px 0px 0px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <img src="/images/ai-xplr-logo-icon.svg" height={18} width={18} alt="xplr-logo" />
              <Typography sx={{ fontSize: '14px', color: '#1A55F9', fontWeight: 400 }}>
                Waiting for user input...
              </Typography>
            </Box>
          )}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <Box
            onClick={() => inputRef.current?.focus()}
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: '12px',
              backgroundColor: '#fff',
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              width: '100%',
              cursor: 'text',
            }}
          >
            {/* Chips for uploaded files and selected contexts */}
            {(contextState?.uploadedFiles?.length > 0 ||
              contextState?.selectedContexts?.length > 0) && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                  {/* File chips */}
                  {!isSending &&
                    contextState?.uploadedFiles
                      ?.filter((file: any) => !file.isAnalyzing)
                      ?.map((file: any) => (
                        <Box
                          key={file.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            border: '1px solid #E0E0E0',
                            borderRadius: '12px',
                            bgcolor: '#fafafa',
                            px: 1,
                            py: 0.5,
                            minWidth: 180,
                            maxWidth: '100%',
                            opacity: file.isUploading || file.isAnalyzing ? 0.6 : 1,
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            if (file?.url) {
                              setFilePreview({ url: file.url, fileName: file.name });
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '6px',
                              bgcolor: '#f1f3f4',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              mr: 1,
                            }}
                          >
                            {file.isUploading || file.isAnalyzing ? (
                              <CircularProgress size={14} sx={{ color: '#5f6368' }} />
                            ) : (
                              getFileIcon(file.name, file.file)
                            )}
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              fontWeight: 500,
                              fontSize: '14px',
                              color: '#202124',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%',
                            }}
                          >
                            {file.name}
                          </Typography>

                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFile(file.id);
                            }}
                            disabled={file.isUploading || file.isAnalyzing}
                            sx={{
                              ml: 0.5,
                              color: '#5f6368',
                              '&:hover': { bgcolor: 'transparent', color: '#000' },
                              '&:disabled': { color: '#ccc' },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      ))}
                  {/* Context chips */}
                  {contextState?.selectedContexts?.map((context: string) => (
                    <Box
                      key={context}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid #E0E0E0',
                        borderRadius: '12px',
                        bgcolor: '#fafafa',
                        px: 1,
                        py: 0.5,
                        minWidth: 180,
                        maxWidth: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '6px',
                          bgcolor: '#f1f3f4',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          mr: 1,
                        }}
                      >
                        <AttachFileIcon
                          sx={{ fontSize: 18, color: '#5f6368', transform: 'rotate(45deg)' }}
                        />
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          fontWeight: 500,
                          fontSize: '14px',
                          color: '#202124',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {getProcessNameById(context)}
                      </Typography>

                      <IconButton
                        size="small"
                        onClick={() => handleRemoveContext(context)}
                        sx={{
                          ml: 0.5,
                          color: '#5f6368',
                          '&:hover': { bgcolor: 'transparent', color: '#000' },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

            {/* Message input */}
            <TextField
              inputRef={inputRef}
              multiline
              maxRows={3}
              placeholder="Message"
              variant="standard"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              onPaste={handlePaste}
              disabled={isSending}
              InputProps={{
                disableUnderline: true,
                sx: { fontSize: '14px', p: 0 },
              }}
            />

            {/* Bottom Row: Buttons */}
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}
            >
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                disabled={isSending}
                component="label"
                sx={{ backgroundColor: '#EEEEEE', p: '6px', borderRadius: '50%' }}
              >
                <AttachFileIcon sx={{ fontSize: 20, transform: 'rotate(45deg)' }} />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {dropdownOptions.map((option) => (
                  <MenuItem
                    key={option}
                    onClick={() => handleDropdownSelect(option)}
                    sx={{ fontSize: '14px' }}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Menu>
              <input ref={fileInputRef} type="file" multiple hidden onChange={handleFileUpload} />

              <IconButton
                onClick={() => handleSendMessage(inputMessage)}
                disabled={isSendDisabled}
                sx={{
                  backgroundColor: 'primary.main',
                  color: '#fff',
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: '#1955F8' },
                  '&.Mui-disabled': {
                    backgroundColor: '#89a7fa',
                  },
                }}
              >
                <UpIcon sx={{ color: '#fff', height: '18px', width: '18px' }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
        {filePreview && (
          <FileViewer
            fileUrl={filePreview.url}
            fileName={filePreview.fileName}
            onClose={() => setFilePreview(null)}
          />
        )}
      </Paper>

      <Modal
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: 24,
            p: 0,
            minWidth: 700,
            maxWidth: 700,
            outline: 'none',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              backgroundColor: '#1A55F9',
              borderRadius: '8px 8px 0 0',
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#fff' }}>
              Delete Ideation
            </Typography>
            <IconButton
              size="small"
              onClick={() => setDeleteDialogOpen(false)}
              sx={{
                color: '#fff',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2 }}>
            <Typography sx={{ fontSize: '14px', color: '#333' }}>
              Do you want to delete this session?
            </Typography>
          </Box>

          {/* Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              p: 2,
              borderTop: '1px solid #E5E5E5',
            }}
          >
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              sx={{
                color: '#666',
                textTransform: 'none',
                fontSize: '14px',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              No
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: '#1A55F9',
                textTransform: 'none',
                fontSize: '14px',
                '&:hover': { backgroundColor: '#1547D9' },
              }}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: 24,
            p: 0,
            minWidth: 700,
            maxWidth: 700,
            outline: 'none',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              backgroundColor: '#1A55F9',
              borderRadius: '8px 8px 0 0',
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#fff' }}>
              Rename Session
            </Typography>
            <IconButton
              size="small"
              onClick={() => setRenameDialogOpen(false)}
              sx={{
                color: '#fff',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3 }}>
            <Typography sx={{ fontSize: '14px', color: '#333', mb: 1.5 }}>Session name</Typography>
            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              value={newSessionName}
              onChange={(e) => setNewSessionName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newSessionName.trim()) {
                  handleRenameSubmit();
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '14px',
                },
              }}
            />
          </Box>

          {/* Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              p: 2,
              borderTop: '1px solid #E5E5E5',
            }}
          >
            <Button
              onClick={() => setRenameDialogOpen(false)}
              sx={{
                color: '#666',
                textTransform: 'none',
                fontSize: '14px',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRenameSubmit}
              variant="contained"
              disabled={!newSessionName.trim()}
              sx={{
                backgroundColor: '#1A55F9',
                textTransform: 'none',
                fontSize: '14px',
                '&:hover': { backgroundColor: '#1547D9' },
                '&.Mui-disabled': {
                  backgroundColor: '#B8C5F0',
                  color: '#fff',
                },
              }}
            >
              Rename
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Resize Handle */}
      <Box
        ref={resizeRef}
        onMouseDown={handleMouseDown}
        sx={{
          position: 'absolute',
          right: 8,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 3,
          height: 21,
          borderRadius: 1,
          backgroundColor: '#B7B7B7',
          cursor: 'ew-resize',
          zIndex: 10,
          transition: 'background-color 0.2s, height 0.2s',
          '&:hover': {
            backgroundColor: 'primary.main',
          },
        }}
      />
    </Box>
  );
};

export default CoiChat;
